<script lang="ts" setup>
import type { Emits, Props } from '.';

import { MAP_CONFIG, MARKER_CONFIG } from '@/composables/useMapGeometry';
import {
	calculateDimensions,
	getCircleRadius,
	getColors,
	getShadowConfig
} from '@/utils/mapGeometryUtils';

interface MarkerLabel {
	text: string;
	color: string;
	fontSize: string;
	fontFamily: string;
	fontWeight: string;
}

interface MarkerIcon {
	url: string;
	anchor: google.maps.Point;
	labelOrigin: google.maps.Point;
	size?: google.maps.Size;
	scaledSize?: google.maps.Size;
}

const emit = defineEmits<Emits>();
const props = defineProps<Props>();

const currentMarker = ref<google.maps.Marker | null>(null);
const isZooming = ref<boolean>(false);
const zoomListener = ref<google.maps.MapsEventListener | null>(null);
const hoverAreaTimeout = ref<ReturnType<typeof setTimeout> | null>(null);

enum MarkerState {
	Normal = 'normal',
	Hovering = 'hovering',
	Selected = 'selected',
	SelectedHovering = 'selectedHovering' // When cluster is selected and the propertylistsection card is hovered
}

const svgCache = new Map<string, string>();
const animationFrameId = ref<number | null>(null);
const currentShadowRadius = ref<number>(MARKER_CONFIG.SHADOW.RADIUS);

// Star SVG path from Figma design
const STAR_PATH =
	'M1.94375 9.56396L2.75625 6.05147L0.03125 3.68896L3.63125 3.37646L5.03125 0.0639648L6.43125 3.37646L10.0312 3.68896L7.30625 6.05147L8.11875 9.56396L5.03125 7.70147L1.94375 9.56396Z';
const STAR_VIEWBOX = { width: 11, height: 10 };

const markerState = ref<MarkerState>(MarkerState.Normal);

const maxZoom = computed(() => props.maxZoom ?? MAP_CONFIG.MAX_ZOOM);
const selectedMarkerSize = computed(
	() => props.clusterSizes?.selected ?? MARKER_CONFIG.TOTAL_SIZES.SELECTED
);
const hoveredMarkerSize = computed(
	() => props.clusterSizes?.hovered ?? MARKER_CONFIG.TOTAL_SIZES.HOVER
);

const SVGUtils = {
	generateCacheKey(config: {
		circleRadius: number;
		shadowRadius: number;
		fillColor: string;
		shadowColor: string;
		shadowOpacity: number;
		isFavorite?: boolean;
	}): string {
		const {
			circleRadius,
			shadowRadius,
			fillColor,
			shadowColor,
			shadowOpacity,
			isFavorite
		} = config;
		return `${circleRadius}-${shadowRadius}-${fillColor}-${shadowColor}-${shadowOpacity}-${isFavorite || false}`;
	},

	createSVGContent(config: {
		svgSize: number;
		center: number;
		circleRadius: number;
		shadowRadius: number;
		fillColor: string;
		shadowColor: string;
		shadowOpacity: number;
		isFavorite?: boolean;
	}): string {
		const {
			svgSize,
			center,
			circleRadius,
			shadowRadius,
			fillColor,
			shadowColor,
			shadowOpacity,
			isFavorite
		} = config;

		let starElement = '';

		if (isFavorite) {
			// Star positioning based on Figma design - top-right corner of circle
			const starSize = Math.max(12, circleRadius * 0.6); // Bigger star relative to circle
			const starDistance = circleRadius + starSize * 0.3; // Position outside the circle to avoid overlapping the number
			const angle = -Math.PI / 4; // 45 degrees up and right (-45 degrees in radians)
			const starX = center + Math.cos(angle) * starDistance;
			const starY = center + Math.sin(angle) * starDistance;

			// Calculate scale to fit the Figma star (11x10) into our desired size
			const scaleX = starSize / STAR_VIEWBOX.width;
			const scaleY = starSize / STAR_VIEWBOX.height;
			const scale = Math.min(scaleX, scaleY);

			// Center the star within its position
			const starOffsetX = starX - (STAR_VIEWBOX.width * scale) / 2;
			const starOffsetY = starY - (STAR_VIEWBOX.height * scale) / 2;

			// Create a bigger background circle for the star
			const starBgRadius = starSize * 0.9;

			// Filled star with white background circle
			starElement = `
				<circle cx="${starX}" cy="${starY}" r="${starBgRadius}" fill="#E2C59E" stroke="none"/>
				<g transform="translate(${starOffsetX}, ${starOffsetY}) scale(${scale})">
				<path d="${STAR_PATH}" fill="#000000"/>
				</g>
				`;
		}

		return `<svg xmlns="http://www.w3.org/2000/svg" width="${svgSize}" height="${svgSize}" viewBox="0 0 ${svgSize} ${svgSize}">
			<circle cx="${center}" cy="${center}" r="${circleRadius + shadowRadius}" fill="${shadowColor}" fill-opacity="${shadowOpacity}"/>
			<circle cx="${center}" cy="${center}" r="${circleRadius}" fill="${fillColor}"/>
			${starElement}
		</svg>`;
	},

	getOrCreateURL(cacheKey: string, svgContent: string): string {
		let url = svgCache.get(cacheKey);

		if (!url) {
			url = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svgContent)}`;

			if (svgCache.size > 100) {
				const firstKey = svgCache.keys().next().value;
				if (firstKey) svgCache.delete(firstKey);
			}

			svgCache.set(cacheKey, url);
		}

		return url;
	}
};

const IconFactory = {
	create(
		circleRadius: number,
		isHovered: boolean,
		isSelected: boolean,
		customShadowRadius?: number,
		isFavorite?: boolean
	): MarkerIcon {
		const shadowRadius = customShadowRadius ?? MARKER_CONFIG.SHADOW.RADIUS;

		const colors = getColors(isHovered, isSelected);
		const shadowConfig = getShadowConfig(isHovered, isSelected);
		const dimensions = calculateDimensions(circleRadius, shadowRadius);

		const cacheKey = SVGUtils.generateCacheKey({
			circleRadius,
			shadowRadius,
			fillColor: colors.fill,
			shadowColor: colors.shadow,
			shadowOpacity: shadowConfig.opacity,
			isFavorite
		});

		const svgContent = SVGUtils.createSVGContent({
			svgSize: dimensions.svgSize,
			center: dimensions.center,
			circleRadius,
			shadowRadius,
			fillColor: colors.fill,
			shadowColor: colors.shadow,
			shadowOpacity: shadowConfig.opacity,
			isFavorite
		});

		const url = SVGUtils.getOrCreateURL(cacheKey, svgContent);

		return {
			url,
			anchor: new google.maps.Point(dimensions.center, dimensions.center),
			labelOrigin: new google.maps.Point(dimensions.center, dimensions.center),
			size: new google.maps.Size(dimensions.svgSize, dimensions.svgSize),
			scaledSize: new google.maps.Size(dimensions.svgSize, dimensions.svgSize)
		};
	}
};

const LabelFactory = {
	create(text: string, isHovered: boolean, isSelected: boolean): MarkerLabel {
		const colors = getColors(isHovered, isSelected);

		return {
			text,
			color: colors.label,
			fontSize: MARKER_CONFIG.LABEL.FONT_SIZE,
			fontFamily: MARKER_CONFIG.LABEL.FONT_FAMILY,
			fontWeight: MARKER_CONFIG.LABEL.FONT_WEIGHT
		};
	}
};

const EventHandlers = {
	mouseOver: () => {
		if (isZooming.value) return;

		if (hoverAreaTimeout.value) {
			clearTimeout(hoverAreaTimeout.value);
			hoverAreaTimeout.value = null;
		}

		if (
			markerState.value === MarkerState.Hovering ||
			markerState.value === MarkerState.SelectedHovering
		) {
			return;
		}

		const hoverTransitions: Record<string, string> = {
			normal: MarkerState.Hovering,
			selected: MarkerState.SelectedHovering
		};
		const newState = hoverTransitions[markerState.value];
		if (newState) {
			markerState.value = newState as typeof markerState.value;
		}
	},

	mouseOut: () => {
		if (isZooming.value) return;

		if (
			markerState.value === MarkerState.Normal ||
			markerState.value === MarkerState.Selected
		) {
			return;
		}

		hoverAreaTimeout.value = setTimeout(() => {
			const unhoverTransitions: Record<string, string> = {
				hovering: MarkerState.Normal,
				selectedHovering: MarkerState.Selected
			};
			const newState = unhoverTransitions[markerState.value];
			if (newState) {
				markerState.value = newState as typeof markerState.value;
			}
			hoverAreaTimeout.value = null;
		}, 50);
	}
};

watch(
	() => props.selectedFromParent,
	newValue => {
		if (newValue || isSelectedState()) {
			markerState.value = newValue ? MarkerState.Selected : MarkerState.Normal;
		}
	},
	{ immediate: true }
);

watch(() => markerState.value, updateMarkerWithAnimation);

watch(
	() => props.clusterSizes,
	() => {
		if (isSelectedState() && !isZooming.value) {
			nextTick(updateMarkerWithAnimation);
		}
	},
	{ deep: true }
);

function isSelectedState(): boolean {
	return (
		markerState.value === MarkerState.Selected ||
		markerState.value === MarkerState.SelectedHovering
	);
}

function calculateTargetShadowRadius(state: MarkerState): number {
	if (!props.cluster) return MARKER_CONFIG.SHADOW.RADIUS;

	const count = props.cluster.properties.pointCount;
	const circleRadius = getCircleRadius(count);

	if (state === MarkerState.Normal) return MARKER_CONFIG.SHADOW.RADIUS;
	if (state === MarkerState.Hovering)
		return hoveredMarkerSize.value / 2 - circleRadius;
	return selectedMarkerSize.value / 2 - circleRadius;
}

function updateMarkerWithAnimation(): void {
	if (!props.cluster) return;

	if (animationFrameId.value) {
		cancelAnimationFrame(animationFrameId.value);
		animationFrameId.value = null;
	}

	const targetShadowRadius = calculateTargetShadowRadius(markerState.value);
	animateMarkerShadow(currentShadowRadius.value, targetShadowRadius);
}

function executeAnimationFrame(
	frames: number[],
	currentFrame: number,
	targetRadius: number
): void {
	if (currentFrame >= frames.length || !currentMarker.value || !props.cluster)
		return;

	const shadowRadius = frames[currentFrame];
	const count = props.cluster.properties.pointCount;
	const circleRadius = getCircleRadius(count);
	const countText = String(count);
	const state = markerState.value;

	const isHovering =
		state === MarkerState.Hovering || state === MarkerState.SelectedHovering;
	const isSelected =
		state === MarkerState.Selected || state === MarkerState.SelectedHovering;

	const icon = IconFactory.create(
		circleRadius,
		isHovering,
		isSelected,
		shadowRadius,
		props.isFavorite
	);
	const label = LabelFactory.create(countText, isHovering, isSelected);

	currentMarker.value.setIcon(icon);
	currentMarker.value.setLabel(label);

	const nextFrame = currentFrame + 1;

	if (nextFrame < frames.length) {
		animationFrameId.value = requestAnimationFrame(() => {
			setTimeout(
				() => executeAnimationFrame(frames, nextFrame, targetRadius),
				40
			);
		});
	} else {
		animationFrameId.value = null;
		currentShadowRadius.value = targetRadius;
	}
}

function animateMarkerShadow(startRadius: number, targetRadius: number): void {
	if (!currentMarker.value || !props.cluster) return;

	const frames = [
		startRadius,
		startRadius + (targetRadius - startRadius) * 0.6,
		targetRadius
	];

	executeAnimationFrame(frames, 0, targetRadius);
}

function createMarkerEventListeners(marker: google.maps.Marker) {
	marker.addListener('mouseover', EventHandlers.mouseOver);
	marker.addListener('mouseout', EventHandlers.mouseOut);
	marker.addListener('click', handleClusterClick);
}

function createZoomListener(map: google.maps.Map) {
	let previousZoom = map.getZoom() ?? 0;

	return map.addListener('zoom_changed', () => {
		isZooming.value = true;

		const currentZoom = map.getZoom() ?? 0;

		// If zoom is less than 16, always clear selection
		if (
			currentZoom < MAP_CONFIG.SELECTION_ZOOM_THRESHOLD &&
			isSelectedState()
		) {
			markerState.value = MarkerState.Normal;

			if (props.cluster) {
				emit('cluster-deselected-by-resolution-change', {
					clusterId: props.cluster.geometry.id
				});
			}
		} else {
			const wasInHighZoomContext =
				previousZoom >= MAP_CONFIG.SELECTION_ZOOM_THRESHOLD;
			const isInHighZoomContext =
				currentZoom >= MAP_CONFIG.SELECTION_ZOOM_THRESHOLD;

			const shouldClearSelection =
				wasInHighZoomContext !== isInHighZoomContext && isSelectedState();

			if (shouldClearSelection) {
				markerState.value = MarkerState.Normal;

				if (props.cluster) {
					emit('cluster-deselected-by-resolution-change', {
						clusterId: props.cluster.geometry.id
					});
				}
			} else if (
				wasInHighZoomContext &&
				isInHighZoomContext &&
				isSelectedState()
			) {
				nextTick(updateMarkerWithAnimation);
			}
		}

		previousZoom = currentZoom;

		removeHoverState();

		const idleListener = map.addListener('idle', () => {
			isZooming.value = false;
			google.maps.event.removeListener(idleListener);
		});
	});
}

function removeHoverState() {
	if (markerState.value === MarkerState.Hovering) {
		markerState.value = MarkerState.Normal;
	}
}

function createGoogleMapsMarker(): google.maps.Marker | null {
	if (!props.map || !props.cluster) return null;

	if (currentMarker.value) {
		removeMarkerFromMap();
	}

	const position = new google.maps.LatLng(
		props.cluster.geometry.coordinates[0],
		props.cluster.geometry.coordinates[1]
	);
	const count = props.cluster.properties.pointCount;
	const circleRadius = getCircleRadius(count);
	const countText = String(count);
	const selectedState = isSelectedState();

	const initialShadowRadius = selectedState
		? selectedMarkerSize.value / 2 - circleRadius
		: MARKER_CONFIG.SHADOW.RADIUS;

	currentShadowRadius.value = initialShadowRadius;

	const marker = new google.maps.Marker({
		position,
		map: props.map,
		label: LabelFactory.create(countText, false, selectedState),
		icon: IconFactory.create(
			circleRadius,
			false,
			selectedState,
			initialShadowRadius,
			props.isFavorite
		),
		zIndex: 1000
	});

	createMarkerEventListeners(marker);
	zoomListener.value = createZoomListener(props.map);

	return marker;
}

function triggerMarkerHover(): void {
	if (!currentMarker.value || !props.cluster || isZooming.value) return;

	EventHandlers.mouseOver();
}

function triggerMarkerUnhover(): void {
	if (!currentMarker.value || !props.cluster || isZooming.value) return;

	EventHandlers.mouseOut();
}

function handleZoomNavigation() {
	if (!props.cluster) return;

	emit('clear-selected-clusters-before-zoom');

	props.map?.panTo(
		new google.maps.LatLng(
			props.cluster.geometry.coordinates[0],
			props.cluster.geometry.coordinates[1]
		)
	);

	setTimeout(() => {
		const currentZoom = props.map?.getZoom() ?? 10;
		let newZoom = currentZoom;
		if (currentZoom < 13) {
			newZoom += 2;
		} else {
			newZoom += 1;
		}

		if (newZoom > maxZoom.value && currentZoom != maxZoom.value) {
			props.map?.setZoom(maxZoom.value);
		} else {
			props.map?.setZoom(newZoom);
		}
	}, 200);

	emit('cluster-pan-zoom', {
		clusterId: props.cluster.geometry.id
	});
}

function handleSelectionAtAnyZoom() {
	if (!props.cluster) return;

	const currentlySelected = isSelectedState();

	emit('cluster-selection-changed', {
		clusterId: props.cluster.geometry.id,
		isSelected: !currentlySelected
	});

	toggleSelection();
}

function handleClusterClick() {
	if (!props.map || !props.cluster) return;

	const currentZoom = props.map.getZoom() ?? 0;
	const clusterCount = props.cluster.properties.pointCount;

	// For zooms 6-15
	if (currentZoom < MAP_CONFIG.SELECTION_ZOOM_THRESHOLD) {
		// If cluster has <= 20 properties, allow selection
		if (clusterCount <= MAP_CONFIG.MAX_SELECTABLE_PROPERTIES) {
			handleSelectionAtAnyZoom();
		} else {
			// If cluster has more than 20 properties, zoom to it
			handleZoomNavigation();
		}
		return;
	}

	if (currentZoom >= MAP_CONFIG.SELECTION_ZOOM_THRESHOLD) {
		handleSelectionAtAnyZoom();
	}
}

function toggleSelection(): void {
	markerState.value = isSelectedState()
		? MarkerState.Normal
		: MarkerState.Selected;
	nextTick(updateMarkerWithAnimation);
}

function removeMarkerFromMap(): void {
	if (currentMarker.value) {
		google.maps.event.clearInstanceListeners(currentMarker.value);
		currentMarker.value.setVisible(false);
		currentMarker.value.setMap(null);
		currentMarker.value = null;
		markerState.value = MarkerState.Normal;
	}

	if (zoomListener.value) {
		google.maps.event.removeListener(zoomListener.value);
		zoomListener.value = null;
	}
}

onMounted(() => {
	if (props.map && props.cluster) {
		currentMarker.value = createGoogleMapsMarker();
	}
});

// Watch for map changes and create marker when available
watch(
	() => props.map,
	newMap => {
		if (newMap && props.cluster && !currentMarker.value) {
			currentMarker.value = createGoogleMapsMarker();
		}
	}
);

onBeforeUnmount(() => {
	if (animationFrameId.value) {
		cancelAnimationFrame(animationFrameId.value);
		animationFrameId.value = null;
	}

	if (hoverAreaTimeout.value) {
		clearTimeout(hoverAreaTimeout.value);
		hoverAreaTimeout.value = null;
	}

	removeMarkerFromMap();
	svgCache.clear();
	isZooming.value = false;
});

defineExpose({
	triggerMarkerHover,
	triggerMarkerUnhover,
	cluster: computed(() => props.cluster)
});
</script>

<template>
	<div class="hidden"></div>
</template>
